const {findArguments, findOptionalArgument} = require("../util/ParserHelpers");
const {getLineWidth} = require("../util/RenderHelpers");
const Font = require("../../util/Font");
const CanvasText = require("../../util/CanvasText");
const DrawingUtil = require("../../util/DrawRectangleUtil");
const AssetsUtil = require("../../util/AssetsUtil");
const ImageCache = require("../../util/ImageCache");
const ArrowDraw = require("../../util/ArrowDraw");
const _ = require("lodash");

// Horizontal compression factor when the [squish] optional argument is used.
// Applied to primary tens and ones advance spacing only; hundreds and
// overlap corrections keep their normal values.
const SQUISH_FACTOR = 0.35;

// Extra vertical space between original and result rows in tenBlock circle mode.
const TEN_BLOCK_CIRCLE_EXTRA_VERTICAL_SPACE = 0.3;

const squishXo = function(amount, squish) {
	return squish ? amount * SQUISH_FACTOR : amount;
};

const sortedParser = function(ps) {
	let optArgs = [];
	const maxOptArgs = 6; // Color, circle tens, tens group count, circle ones, ones group count, squish
	for (let optArgIndex = 0; optArgIndex < maxOptArgs; optArgIndex++) {
		const optArg = findOptionalArgument(ps);
		if (optArg && optArg.text) optArgs.push(optArg.text);
	}

	// Check for color optional argument, set it, then remove from args being considered
	const colorArgIndex = _.findIndex(optArgs, a => /^#[A-Za-z\d]{6}$/.test(a));
	let color;
	if (colorArgIndex > -1) {
		color = optArgs[colorArgIndex].substring(1);
		optArgs = _.filter(optArgs, a => !/^#[A-Za-z\d]{6}$/.test(a));
	}

	// Iterate through the rest of the arguments
	// Expected format: circle=x or circle=o, potentially followed
	//   by an additional numerical argument for how many groups of 10
	//   can be circled
	// Ignore anything that doesn't conform to that pattern
	let circleOnes;
	let maxCircledOneGroups = 1;
	let circleTens;
	let maxCircledTenGroups = 1;
	for (
		let circleArgIndex = 0;
		circleArgIndex < optArgs.length;
		circleArgIndex++
	) {
		const optArg = optArgs[circleArgIndex];
		if (optArg === "circle=o") {
			circleOnes = true;
			const nextArg = parseInt(optArgs[circleArgIndex + 1]);
			if (_.isInteger(nextArg)) {
				maxCircledOneGroups = nextArg;
				circleArgIndex++;
			}
		}
		if (optArg === "circle=x") {
			circleTens = true;
			const nextArg = parseInt(optArgs[circleArgIndex + 1]);
			if (_.isInteger(nextArg)) {
				maxCircledTenGroups = nextArg;
				circleArgIndex++;
			}
		}
	}

	const squish = _.includes(optArgs, "squish");

	const args = findArguments(ps, 3);
	//number of each symbol in original row
	const ogHundreds = parseInt(args[0].text);
	const ogTens = parseInt(args[1].text);
	const ogOnes = parseInt(args[2].text);
	let hundreds = ogHundreds;
	let tens = ogTens;
	let ones = ogOnes;

	//makes an array indicating order of symbols
	const makePirateArray = function(hun, ten, one) {
		const pirateArray = [];
		for (let i = 0; i < hun; i++) pirateArray.push("c");
		for (let i = 0; i < ten; i++) pirateArray.push("x");
		for (let i = 0; i < one; i++) pirateArray.push("o");
		return pirateArray;
	};

	const pirateArr = makePirateArray(hundreds, tens, ones);
	//if user desires circling, then this will hold information on indices important
	//to mapping the original pirate array to the resulting pirate array
	let arrowPositionOnes = -1;
	let arrowPositionTens = -1;
	//is the optional argument one of the valid types
	let needResultOnes = 0;
	let needResultTens = 0;

	if (circleTens && ogTens >= 10) {
		needResultTens = maxCircledTenGroups;
		needResultTens =
			needResultTens > parseInt(tens / 10)
				? parseInt(tens / 10)
				: needResultTens;
		arrowPositionTens = [];
		for (let i = 0; i < needResultTens; i++) {
			hundreds += 1;
			tens -= 10;
			arrowPositionTens.push({
				arrowStart: ogHundreds + 10 * i + 5,
				arrowEndResult: hundreds - 1,
			});
		}
	}
	if (circleOnes && ogOnes >= 10) {
		needResultOnes = maxCircledOneGroups;
		needResultOnes =
			needResultOnes > parseInt(ones / 10)
				? parseInt(ones / 10)
				: needResultOnes;
		arrowPositionOnes = [];
		for (let i = 0; i < needResultOnes; i++) {
			tens += 1;
			ones -= 10;
			arrowPositionOnes.push({
				arrowStart: ogHundreds + ogTens + 10 * i + 5,
				arrowEndResult: hundreds + tens - 1,
			});
		}
	}
	const circleResult =
		needResultOnes || needResultTens
			? makePirateArray(hundreds, tens, ones)
			: [];

	return [
		{
			type: "pirateNumber",
			pirateArr,
			circleResult,
			arrowPositionOnes,
			arrowPositionTens,
			needResultOnes,
			needResultTens,
			children: [],
			color,
			squish,
			assetType: "standard",
		},
	];
};

const unsortedParser = function(ps) {
	// get the color
	const optArgs = [];
	for (let optArgIndex = 0; optArgIndex < 5; optArgIndex++) {
		const optArg = findOptionalArgument(ps);
		if (optArg && optArg.text) optArgs.push(optArg.text);
	}

	const colorArgIndex = _.findIndex(optArgs, a => /^#[0-9A-Fa-f]{6}$/.test(a));
	let color;
	if (colorArgIndex > -1) {
		color = optArgs[colorArgIndex];
		if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
			ps.pushError(
				`"${color}" is not a valid hex color. Please input a six-digit hex color code (with # prefix, ex: #233E9B). ` +
					`Note: check with a BA dev for approved color codes.`
			);
			color = "";
		} else {
			color = _.toUpper(color.substring(1));
		}
	}

	const squish = _.includes(optArgs, "squish");
	// get the list of characters
	const args = findArguments(ps, 1);
	if (!args[0]) return null;
	const pirateStr = args[0].text;
	if (!/^[cxo]*$/.test(pirateStr)) {
		ps.pushError(
			`"${pirateStr}" is not a valid pirate number string. Valid characters are c, x, and o.`
		);
	}
	const pirateArr = pirateStr.split("");

	return [
		{
			type: "unsortedPirateNumber",
			pirateArr,
			children: [],
			color,
			squish,
			assetType: "standard",
		},
	];
};

const b10Parser = function(ps) {
	const nodes = sortedParser(ps);
	nodes[0].assetType = "tenBlock";
	return nodes;
};

const unsortedB10Parser = function(ps) {
	const nodes = unsortedParser(ps);
	nodes[0].assetType = "tenBlock";
	return nodes;
};

const sortedNodeDef = {
	draw(rc, x, y, node, measure) {
		const ctx = rc.context;
		ctx.save();
		const urlFor = symbolName => {
			let assetName = symbolName;
			if (node.assetType === "tenBlock") {
				if (symbolName === "c") assetName = "square";
				else if (symbolName === "x") assetName = "line";
				else if (symbolName === "dot") assetName = "circle";
			}
			return AssetsUtil.getPirateImage(assetName, node.color);
		};
		const imgSrcC = urlFor("c");
		const imgSrcX = urlFor("x");
		const imgSrcO = urlFor("dot");
		const squish = node.squish && node.assetType === "tenBlock";
		const pirateC = ImageCache.src(imgSrcC, true);
		const pirateX = ImageCache.src(imgSrcX, true);
		const pirateO = ImageCache.src(imgSrcO, true);
		const fontSize = measure.fontInfo.size;
		const symbolDim = measure.symbolDim;
		ctx.lineWidth = getLineWidth(rc, measure.fontInfo);
		const arrowPositionOnes = node.arrowPositionOnes;
		const arrowPositionTens = node.arrowPositionTens;
		const pirateArr = node.pirateArr;
		const originalStarts = _.map(measure.symbolStarts, val => val + x);

		//renders the pirate array starting with specified coordinates arrX and arrY.
		//Symbol positions are specified in startX. Note that arrX is initially added to
		//each element of startX.
		const renderPirateArray = function(pirateArray, arrX, arrY, startX) {
			const adjustedX = _.map(startX, val => val + arrX);
			_.each(pirateArray, (val, i) => {
				if (val === "c") {
					ctx.drawImage(pirateC, adjustedX[i], arrY, symbolDim, symbolDim);
				} else if (val === "x") {
					const correctionWidth =
						node.assetType === "tenBlock"
							? (pirateX.width / pirateX.height) * symbolDim
							: symbolDim;
					const correctionOffset =
						node.assetType === "tenBlock"
							? (symbolDim - correctionWidth) / 2
							: 0;
					ctx.drawImage(
						pirateX,
						adjustedX[i] + correctionOffset,
						arrY,
						correctionWidth,
						symbolDim
					);
				} else if (val === "o") {
					const correctionHeight =
						node.assetType === "tenBlock"
							? (pirateO.height / pirateO.width) * symbolDim
							: symbolDim;
					const correctionOffset =
						node.assetType === "tenBlock"
							? (symbolDim - correctionHeight) / 2
							: 0;
					const imgPadding = node.assetType === "tenBlock" ? symbolDim / 18 : 0;
					ctx.drawImage(
						pirateO,
						adjustedX[i] + imgPadding,
						arrY + imgPadding + correctionOffset,
						symbolDim - 2 * imgPadding,
						correctionHeight - imgPadding * 2
					);
				}
			});
			return adjustedX;
		};

		//draw the original series of pirate symbols and get their starting points
		renderPirateArray(
			pirateArr,
			x,
			y - measure.top - 0.125 * fontSize,
			measure.symbolStarts
		);

		if (node.needResultTens || node.needResultOnes) {
			const resultSymbolStarts = measure.resultSymbolStarts;
			ctx.font = Font.fromInfo(measure.fontInfo);
			const arrowX = node.needResultTens
				? originalStarts[arrowPositionTens[0].arrowStart]
				: originalStarts[arrowPositionOnes[0].arrowStart];
			const arrowXEnd = node.needResultTens
				? resultSymbolStarts[arrowPositionTens[0].arrowEndResult]
				: resultSymbolStarts[arrowPositionOnes[0].arrowEndResult];
			const resultX =
				arrowX - symbolDim / 2 - arrowXEnd + resultSymbolStarts[0];
			const resultY = y + measure.resultRowYOffset;
			const equalsX = resultX - measure.equalsMeasure;
			const equalsY = y + 0.5 * measure.bottom;
			CanvasText.drawMiddleText(ctx, "=", equalsX, equalsY);
			renderPirateArray(
				node.circleResult,
				resultX,
				resultY,
				resultSymbolStarts
			);
		}
		const ARROW_LEFT_SHIFT = 0.3 * fontSize;
		const arrowAnchorX = function(absoluteX) {
			return absoluteX - ARROW_LEFT_SHIFT;
		};
		let isTenRemoved = node.needResultTens;
		while (isTenRemoved) {
			const cornerRadius = (1 / 4) * fontSize;
			const resultSymbolStarts = measure.resultSymbolStarts;
			const index = arrowPositionTens.length - isTenRemoved;
			const startShift = squish ? 0.4 * fontSize : 0.18 * fontSize;
			const endShift = squish ? 0.65 * symbolDim : symbolDim / 1.2;
			const arrowX0 = originalStarts[arrowPositionTens[0].arrowStart];
			const resultX =
				arrowX0 -
				symbolDim / 1.25 -
				resultSymbolStarts[arrowPositionTens[0].arrowEndResult] +
				resultSymbolStarts[1];
			const arrowX = arrowAnchorX(
				originalStarts[arrowPositionTens[index].arrowStart]
			);
			const arrowXEnd = arrowAnchorX(
				resultSymbolStarts[arrowPositionTens[index].arrowEndResult] + resultX
			);

			const arrowRadius = 0.3 * fontSize;
			const rectStartX =
				originalStarts[arrowPositionTens[index].arrowStart - 5] + startShift;
			const rectStartY = y - 0.975 * measure.top;
			const rectEnd =
				originalStarts[arrowPositionTens[index].arrowStart + 4] + endShift;
			const rectWidth = rectEnd - rectStartX;
			const rectHeight = 0.75 * symbolDim;
			const arcCenterY = rectStartY + rectHeight + arrowRadius;
			const arrowParams = {
				x1: arrowXEnd + fontSize / 10,
				y1: 3 * arrowRadius + y - 0.9 * measure.top + 0.575 * fontSize,
				x2: arrowXEnd - fontSize / 10,
				y2: 3 * arrowRadius + y - 0.9 * measure.top + 0.575 * fontSize,
				length: (1 / 3.5) * fontSize,
				curved: true,
			};
			//render the result object and the equals node
			//draw the rounded rect
			DrawingUtil.drawRoundedRect(
				ctx,
				rectStartX,
				rectStartY,
				rectWidth,
				rectHeight,
				cornerRadius
			);
			ctx.stroke();
			if (index === 0) {
				//draw the arrow's tail
				ctx.bp();
				ctx.arc(
					arrowXEnd + arrowRadius,
					arcCenterY,
					arrowRadius,
					-Math.PI / 2,
					-Math.PI,
					true
				);
				ctx
					.lineTo(
						arrowXEnd,
						3 * arrowRadius + y - 0.9 * measure.top + 0.7 * fontSize
					)
					.stroke();
			} else {
				//draw the arrow's tail
				ctx.bp();
				ctx.arc(arrowX, arcCenterY - arrowRadius, arrowRadius, 0, Math.PI / 2);
				ctx.lineTo(arrowXEnd + arrowRadius, arcCenterY).stroke();
				ctx.arcTo(arrowXEnd, arcCenterY, arrowXEnd, y, arrowRadius).stroke();
				ctx.lineTo(arrowXEnd, y).stroke();
			}
			//draw the arrowhead
			ctx.bp();
			ArrowDraw.drawArrowHead(ctx, arrowParams);

			isTenRemoved -= 1;
		}
		let isOneRemoved = node.needResultOnes;
		while (isOneRemoved) {
			const cornerRadius = (1 / 4) * fontSize;
			const resultSymbolStarts = measure.resultSymbolStarts;
			const index = arrowPositionOnes.length - isOneRemoved;
			const unsquishedStartShift =
				node.assetType === "tenBlock"
					? symbolDim / 3.1 - symbolDim / 2.5
					: symbolDim / 3.2;
			const unsquishedEndShift =
				node.assetType === "tenBlock"
					? symbolDim / 1.45 + symbolDim / 2.5
					: symbolDim / 1.45;
			const startShift = squish ? 0.4 * fontSize : unsquishedStartShift;
			const endShift = squish ? 0.65 * symbolDim : unsquishedEndShift;

			const arrowX0 = originalStarts[arrowPositionOnes[0].arrowStart];
			const resultX = node.needResultTens
				? originalStarts[arrowPositionTens[0].arrowStart] -
				  symbolDim / 1.25 -
				  resultSymbolStarts[arrowPositionTens[0].arrowEndResult] +
				  resultSymbolStarts[1]
				: arrowX0 -
				  symbolDim / 1.4 -
				  resultSymbolStarts[arrowPositionOnes[0].arrowEndResult] +
				  resultSymbolStarts[1];
			const arrowX = arrowAnchorX(
				originalStarts[arrowPositionOnes[index].arrowStart]
			);
			const arrowXEnd = arrowAnchorX(
				resultSymbolStarts[arrowPositionOnes[index].arrowEndResult] +
					resultX -
					(node.assetType === "tenBlock" ? symbolDim / 5 : 0)
			);

			const arrowRadius = 0.3 * fontSize;
			const rectStartX =
				originalStarts[arrowPositionOnes[index].arrowStart - 5] + startShift;
			const rectStartY =
				y - (node.assetType === "tenBlock" ? 1.05 : 0.9) * measure.top;
			const rectEnd =
				originalStarts[arrowPositionOnes[index].arrowStart + 4] + endShift;
			const rectWidth = rectEnd - rectStartX;
			const rectHeight =
				(node.assetType === "tenBlock" ? 1.15 : 0.7) * fontSize;
			const arcCenterY =
				y -
				0.9 * measure.top +
				(node.assetType === "tenBlock" ? 1 : 0.7) * fontSize +
				arrowRadius;
			const arrowParams = {
				x1: arrowXEnd + fontSize / 10,
				y1: 2 * arrowRadius + arcCenterY - fontSize / 8,
				x2: arrowXEnd - fontSize / 10,
				y2: 2 * arrowRadius + arcCenterY - fontSize / 8,
				length: (1 / 3.5) * fontSize,
				curved: true,
			};
			//render the result object and the equals node
			//draw the rounded rect
			DrawingUtil.drawRoundedRect(
				ctx,
				rectStartX,
				rectStartY,
				rectWidth,
				rectHeight,
				cornerRadius
			);
			ctx.stroke();
			if (index === 0 && !node.needResultTens) {
				//draw the arrow's tail
				ctx.bp();
				ctx.arc(
					arrowXEnd + arrowRadius,
					arcCenterY,
					arrowRadius,
					-Math.PI / 2,
					-Math.PI,
					true
				);
				ctx
					.lineTo(
						arrowXEnd,
						3 * arrowRadius + y - 0.9 * measure.top + 0.7 * fontSize
					)
					.stroke();
			} else {
				//draw the arrow's tail
				ctx.bp();
				ctx.arc(arrowX, arcCenterY - arrowRadius, arrowRadius, 0, Math.PI / 2);
				ctx.lineTo(arrowXEnd + arrowRadius, arcCenterY).stroke();
				ctx.arcTo(arrowXEnd, arcCenterY, arrowXEnd, y, arrowRadius).stroke();
				ctx.lineTo(arrowXEnd, y).stroke();
			}
			//draw the arrowhead
			ctx.bp();
			ArrowDraw.drawArrowHead(ctx, arrowParams);

			isOneRemoved -= 1;
		}
		ctx.restore();
	},
	measure(rc, fontInfo, props, node) {
		const ctx = rc.context;
		const overlap = 0.2 * fontInfo.size; //overlap amount upon symbol change
		const cOverlap = 1.6 * overlap; //overlap between pirate C's
		const cEndPad = 0.4 * overlap; //padding to use if last symbol is a pirateC
		const xEndPad = 1.4 * overlap; //padding to use if last symbol is a pirateX
		const oOverlap = 2.4 * overlap; //overlap between pirate O's
		const oEndPad = 0.2 * overlap; //padding to use if last symbol is a pirateDot
		const symbolDim = 1.25 * fontInfo.size; //length and width for each symbol image
		const arrowPositionOnes = node.arrowPositionOnes;
		const arrowPositionTens = node.arrowPositionTens;
		const pirateArr = node.pirateArr;
		const squish = node.squish && node.assetType === "tenBlock";

		const measurePirateArray = function(pirateArray) {
			let width = 0;
			const symbolStarts = [];
			_.each(pirateArray, (val, i) => {
				if (val === "c") {
					symbolStarts.push(width);
					width += (node.assetType === "tenBlock" ? 1.5 : 1) * fontInfo.size;
				} else if (val === "x") {
					if (i !== 0 && pirateArray[i - 1] === "c")
						width += overlap * (node.assetType === "tenBlock" ? -0.5 : 1);

					if (node.needResultTens) {
						if (
							i === arrowPositionTens.arrowEndResult ||
							i === arrowPositionTens.arrowEndResult + 10
						)
							width += overlap;
					}

					symbolStarts.push(width);
					width += squishXo(
						(node.assetType === "tenBlock" ? 0.9 : 1.25) * fontInfo.size,
						squish
					);
				} else if (val === "o") {
					if (i !== 0 && pirateArray[i - 1] === "x")
						width -= (node.assetType === "tenBlock" ? -1.5 : 0.8) * overlap;

					if (node.needResultOnes) {
						if (i === arrowPositionOnes.arrowEndResult) width += overlap;
						else if (i === arrowPositionOnes.arrowEndResult + 10)
							width += 0.5 * overlap;
					}
					symbolStarts.push(width);
					width += squishXo(
						(node.assetType === "tenBlock" ? 1.4 : 1.0) * fontInfo.size,
						squish
					);
				}
			});

			return {
				symbolStarts,
				width,
			};
		};
		const pirateArrMeasures = measurePirateArray(pirateArr);
		let objWidth = pirateArrMeasures.width;
		const resultArrMeasures =
			node.needResultOnes || node.needResultTens
				? measurePirateArray(node.circleResult)
				: {};

		// add back overlap that was removed after last number
		const lastSymbol = pirateArr[pirateArr.length - 1];
		if (lastSymbol === "x") objWidth += squishXo(xEndPad, squish);
		else if (lastSymbol === "o")
			objWidth += squishXo(0.5 * oOverlap + oEndPad, squish);
		else objWidth += cOverlap + cEndPad;

		// font size used instead of symbolDim to make object height appear more
		// vertically symmetrical, especially when optional argument isn't used.
		let objHeight = fontInfo.size;
		let arrowPadding = fontInfo.size;
		let resultRowYOffset = 0.25 * fontInfo.size;
		if (node.circleResult.length > 0) {
			const extraCircleRowGap =
				node.assetType === "tenBlock"
					? TEN_BLOCK_CIRCLE_EXTRA_VERTICAL_SPACE * fontInfo.size
					: 0;
			arrowPadding = fontInfo.size + extraCircleRowGap;
			resultRowYOffset = 0.25 * fontInfo.size + extraCircleRowGap;
			objHeight = 2 * symbolDim + arrowPadding;
		}

		//measure equal sign
		ctx.save();
		ctx.font = Font.fromInfo(fontInfo);
		const equalsMeasure = ctx.measureText("=").width;
		ctx.restore();

		return {
			width: objWidth,
			height: objHeight,
			top: objHeight / 2,
			bottom: objHeight / 2,
			fontInfo,
			symbolDim,
			overlap,
			symbolStarts: pirateArrMeasures.symbolStarts,
			resultSymbolStarts: resultArrMeasures.symbolStarts,
			equalsMeasure,
			resultRowYOffset,
		};
	},
	readAloud: {special: true},
};

const unsortedNodeDef = {
	draw(rc, x, y, node, measure) {
		const ctx = rc.context;
		ctx.save();
		const urlFor = symbolName => {
			let assetName = symbolName;
			if (node.assetType === "tenBlock") {
				if (symbolName === "c") assetName = "square";
				else if (symbolName === "x") assetName = "line";
				else if (symbolName === "dot") assetName = "circle";
			}
			return AssetsUtil.getPirateImage(assetName, node.color);
		};
		const imgSrcC = urlFor("c");
		const imgSrcX = urlFor("x");
		const imgSrcO = urlFor("dot");
		const pirateC = ImageCache.src(imgSrcC, true);
		const pirateX = ImageCache.src(imgSrcX, true);
		const pirateO = ImageCache.src(imgSrcO, true);
		const fontSize = measure.fontInfo.size;
		const symbolDim = measure.symbolDim;
		ctx.lineWidth = getLineWidth(rc, measure.fontInfo);
		const pirateArr = node.pirateArr;

		//renders the pirate array starting with specified coordinates arrX and arrY.
		//Symbol positions are specified in startX. Note that arrX is initially added to
		//each element of startX.
		const renderPirateArray = function(pirateArray, arrX, arrY, startX) {
			const adjustedX = _.map(startX, val => val + arrX);
			_.each(pirateArray, (val, i) => {
				if (val === "c") {
					const imgPadding = node.assetType === "tenBlock" ? symbolDim / 10 : 0;
					ctx.drawImage(
						pirateC,
						adjustedX[i] + imgPadding,
						arrY + imgPadding,
						symbolDim - 2 * imgPadding,
						symbolDim - 2 * imgPadding
					);
				} else if (val === "x") {
					const correctionWidth =
						node.assetType === "tenBlock"
							? (pirateX.width / pirateX.height) * symbolDim
							: symbolDim;
					const correctionOffset =
						node.assetType === "tenBlock"
							? (symbolDim - correctionWidth) / 2
							: 0;
					ctx.drawImage(
						pirateX,
						adjustedX[i] + correctionOffset,
						arrY,
						correctionWidth,
						symbolDim
					);
				} else if (val === "o") {
					const correctionHeight =
						node.assetType === "tenBlock"
							? (pirateO.height / pirateO.width) * symbolDim
							: symbolDim;
					const correctionOffset =
						node.assetType === "tenBlock"
							? (symbolDim - correctionHeight) / 2
							: 0;
					const imgPadding = node.assetType === "tenBlock" ? symbolDim / 9 : 0;
					ctx.drawImage(
						pirateO,
						adjustedX[i] + imgPadding,
						arrY + correctionOffset + imgPadding,
						symbolDim - 2 * imgPadding,
						correctionHeight - 2 * imgPadding
					);
				}
			});
		};

		//draw the pirate symbols and get their starting points
		renderPirateArray(
			pirateArr,
			x,
			y - measure.top - 0.125 * fontSize,
			measure.symbolStarts
		);

		ctx.restore();
	},
	measure(rc, fontInfo, props, node) {
		const symbolDim = 1.25 * fontInfo.size; //length and width for each symbol image
		const pirateArr = node.pirateArr;
		const squish = node.squish && node.assetType === "tenBlock";

		const measurePirateArr = function(pirateArray) {
			let width = 0;
			const symbolStarts = [];
			_.each(pirateArray, (val, i) => {
				if (val === "c") {
					symbolStarts.push(width);
					// Base width minus right overlap
					width +=
						(node.assetType === "tenBlock" ? 1 : 1.0 - 0.18) * fontInfo.size;
				} else if (val === "x") {
					symbolStarts.push(width);
					// Base width minus right overlap
					width += squishXo(
						(node.assetType === "tenBlock" ? 0.5 : 1.25 - 0.3) * fontInfo.size,
						squish
					);
					// Next symbol isn't a 10 (for bten assets only)
					if (
						node.assetType === "tenBlock" &&
						i < pirateArray.length - 1 &&
						pirateArray[i + 1] !== "x"
					)
						width += 0.5 * fontInfo.size;
				} else if (val === "o") {
					// Shift starting point for dots left a bit
					symbolStarts.push(
						width - (node.assetType === "tenBlock" ? 0 : 0.1) * fontInfo.size
					);
					// Base width minus right overlap
					width += squishXo(
						(node.assetType === "tenBlock" ? 1 : 1.0 - 0.4) * fontInfo.size,
						squish
					);
				}
			});

			return {
				symbolStarts,
				width,
			};
		};
		const pirateArrMeasures = measurePirateArr(pirateArr);
		const lastSymbol = pirateArr[pirateArr.length - 1];
		const endPad = 0.25 * fontInfo.size;
		const objWidth =
			pirateArrMeasures.width +
			(lastSymbol === "c" ? endPad : squishXo(endPad, squish));
		const objHeight = fontInfo.size;

		return {
			width: objWidth,
			height: objHeight,
			top: objHeight / 2,
			bottom: objHeight / 2,
			fontInfo,
			symbolDim,
			symbolStarts: pirateArrMeasures.symbolStarts,
		};
	},
	readAloud: {special: true},
};

module.exports = {
	parsers: {
		mathCommands: {
			pirateNumber: sortedParser,
			unsortedPirateNumber: unsortedParser,
			btenPirateNumber: b10Parser,
			unsortedBtenPirateNumber: unsortedB10Parser,
		},
	},
	nodes: {
		pirateNumber: sortedNodeDef,
		unsortedPirateNumber: unsortedNodeDef,
		btenPirateNumber: sortedNodeDef,
		unsortedBtenPirateNumber: unsortedNodeDef,
	},
};
